import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  FiSave,
  FiEye,
  FiArrowLeft,
  FiLoader,
  FiCheck,
  FiAlertCircle,
  FiCalendar,
  FiArrowDown,
  FiFileText,
  FiSearch,
  FiUsers,
  FiClock,
} from "react-icons/fi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BlockEditorPanel,
  EMAIL_BRAND,
  createBlock,
  blocksToMarkdown,
  markdownToBlocks,
  contentToHtml,
  interpolatePreview,
} from "@/components/email";
import type { ContentBlock } from "@/components/email";

// ─── Types ────────────────────────────────────────────────────────────────────

type Campaign = {
  id: string;
  name: string;
  subject: string;
  previewText: string | null;
  content: string;
  status: "draft" | "scheduled" | "sending" | "sent" | "cancelled";
  segment: string;
  excludedEmails: string | null;
  scheduledFor: string | null;
  createdAt: string;
};

type CustomerSummary = {
  id: string;
  email: string;
  name: string | null;
  accountStatus: string | null;
};

type FormData = {
  name: string;
  subject: string;
  previewText: string;
  segment: string;
  scheduledFor: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

const segmentOptions = [
  { value: "all", label: "All Subscribers" },
  { value: "newsletter", label: "Newsletter Subscribers" },
  { value: "rate_alerts", label: "Rate Alert Subscribers" },
  { value: "market_updates", label: "Market Update Subscribers" },
  { value: "promotions", label: "Promotions Subscribers" },
  { value: "existing", label: "Existing (Paying) Customers" },
  { value: "ineligible", label: "Ineligible Customers" },
  { value: "discharged", label: "Discharged Customers" },
];

// ─── Campaign Templates ──────────────────────────────────────────────────────

const CAMPAIGN_TEMPLATES = [
  {
    name: "Market Update",
    description: "Monthly market & rate update",
    blocks: [
      createBlock("heading", { content: "Market Update", level: 1 }),
      createBlock("text", { content: "Hi {{firstName}},\n\nHere's your latest market update with the most recent developments in the Australian property and lending landscape." }),
      createBlock("divider"),
      createBlock("heading", { content: "Interest Rates", level: 2 }),
      createBlock("text", { content: "The RBA has made its latest decision on the cash rate. Here's what it means for you and your mortgage." }),
      createBlock("callout", { content: "The RBA cash rate is currently X.XX%. This is a change of X.XX% from the previous month.", alt: "info" }),
      createBlock("heading", { content: "Property Market", level: 2 }),
      createBlock("two_column", { content: "**Sydney & Melbourne**\nMedian prices continue to...", content2: "**Brisbane & QLD**\nThe Southeast Queensland market..." }),
      createBlock("divider"),
      createBlock("text", { content: "If you'd like to discuss how these changes affect your situation, please don't hesitate to get in touch." }),
      createBlock("button", { content: "Book a Chat", url: "https://pathfinancial.com.au/contact" }),
      createBlock("signature", { content: "Warm regards," }),
    ],
  },
  {
    name: "Newsletter",
    description: "General newsletter template",
    blocks: [
      createBlock("heading", { content: "Path Financial Newsletter", level: 1 }),
      createBlock("text", { content: "Hi {{firstName}},\n\nWelcome to this month's edition of our newsletter. We've got some great content to share with you." }),
      createBlock("image", { url: "", alt: "Newsletter header image" }),
      createBlock("heading", { content: "Featured Article", level: 2 }),
      createBlock("text", { content: "Your featured article content goes here. Share insights, tips, or news that's relevant to your customers." }),
      createBlock("divider"),
      createBlock("heading", { content: "Tips & Advice", level: 2 }),
      createBlock("list", { content: "Financial tip one\nFinancial tip two\nFinancial tip three" }),
      createBlock("callout", { content: "Did you know? You could save thousands by reviewing your home loan every 2-3 years.", alt: "tip" }),
      createBlock("divider"),
      createBlock("heading", { content: "What Our Clients Say", level: 2 }),
      createBlock("quote", { content: "CJ made the whole process so easy. We couldn't be happier with our new home loan!", alt: "Sarah M., First Home Buyer" }),
      createBlock("spacer"),
      createBlock("text", { content: "Thanks for reading! If you have any questions, we're always here to help." }),
      createBlock("button", { content: "Get in Touch", url: "https://pathfinancial.com.au/contact" }),
      createBlock("signature", { content: "Thanks for reading!" }),
    ],
  },
  {
    name: "Video Announcement",
    description: "Share a video with your audience",
    blocks: [
      createBlock("heading", { content: "Something Special For You", level: 1 }),
      createBlock("text", { content: "Hi {{firstName}},\n\nWe've put together a short video we think you'll find really valuable." }),
      createBlock("video", { content: "Watch the Video", url: "", alt: "" }),
      createBlock("divider"),
      createBlock("text", { content: "In this video, we cover:" }),
      createBlock("list", { content: "Key topic one\nKey topic two\nKey topic three" }),
      createBlock("spacer"),
      createBlock("text", { content: "Have questions about what you've seen? We'd love to chat." }),
      createBlock("button", { content: "Book a Call", url: "https://pathfinancial.com.au/contact" }),
      createBlock("signature", { content: "Warm regards," }),
    ],
  },
  {
    name: "Client Testimonial",
    description: "Share success stories",
    blocks: [
      createBlock("heading", { content: "Another Happy Home Owner!", level: 1 }),
      createBlock("text", { content: "Hi {{firstName}},\n\nWe love helping Australians achieve their property dreams. Here's a recent success story we wanted to share with you." }),
      createBlock("divider"),
      createBlock("image", { url: "", alt: "Client success photo" }),
      createBlock("quote", { content: "We never thought owning a home was possible, but CJ and the team at Path Financial made it happen. The process was smooth from start to finish!", alt: "James & Emma, Springfield" }),
      createBlock("callout", { content: "This family saved over $12,000 in interest over the life of their loan by choosing the right product.", alt: "tip" }),
      createBlock("divider"),
      createBlock("text", { content: "Whether you're buying your first home, refinancing, or investing, we're here to help you find the best deal." }),
      createBlock("button", { content: "Start Your Journey", url: "https://pathfinancial.com.au/contact" }),
      createBlock("signature", { content: "Warm regards," }),
    ],
  },
  {
    name: "Blank",
    description: "Start from scratch",
    blocks: [createBlock("text")],
  },
];

/**
 * Format a UTC instant (Date) as a "YYYY-MM-DDTHH:mm" string in the user's
 * local timezone, suitable for an `<input type="datetime-local">` value.
 *
 * Why we cannot just call `.toISOString().slice(0, 16)`:
 *   That returns the UTC components, but datetime-local inputs render whatever
 *   string they're handed as if it were local time. The result is a campaign
 *   that was scheduled for, say, 11pm AEST showing 1pm in the input — and
 *   any countdown derived from that input is then off by the timezone offset.
 *
 * Implementation: subtract the timezone offset (which getTimezoneOffset
 * reports in MINUTES, with the opposite sign of the conventional offset) from
 * the UTC time to land on the local wall-clock components, then format with
 * toISOString and slice.
 */
function utcInstantToLocalInputValue(instant: Date): string {
  const tzOffsetMs = instant.getTimezoneOffset() * 60_000;
  const local = new Date(instant.getTime() - tzOffsetMs);
  return local.toISOString().slice(0, 16);
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function CampaignEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [showPreview, setShowPreview] = useState(true);
  const [allCustomers, setAllCustomers] = useState<CustomerSummary[]>([]);
  const [allSubscribers, setAllSubscribers] = useState<{ email: string; name: string | null; status: string }[]>([]);
  const [excludedEmails, setExcludedEmails] = useState<string[]>([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [showRecipientList, setShowRecipientList] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    subject: "",
    previewText: "",
    segment: "all",
    scheduledFor: "",
  });

  const [blocks, setBlocks] = useState<ContentBlock[]>([createBlock("text")]);
  const [countdown, setCountdown] = useState("");

  // Live countdown timer for scheduled campaigns
  useEffect(() => {
    if (!formData.scheduledFor) { setCountdown(""); return; }
    const update = () => {
      const diff = new Date(formData.scheduledFor).getTime() - Date.now();
      if (diff <= 0) { setCountdown("Sending now..."); return; }
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setCountdown(d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [formData.scheduledFor]);

  // Derived markdown content from blocks
  const markdownContent = useMemo(() => blocksToMarkdown(blocks), [blocks]);

  // Validation
  const validation = useMemo(() => ({
    name: {
      valid: formData.name.length >= 1 && formData.name.length <= 100,
      message: formData.name.length === 0
        ? "Required"
        : formData.name.length > 100
          ? `${formData.name.length}/100 - too long`
          : `${formData.name.length}/100 characters`,
    },
    subject: {
      valid: formData.subject.length >= 1 && formData.subject.length <= 150,
      message: formData.subject.length === 0
        ? "Required - What recipients will see in their inbox"
        : formData.subject.length > 150
          ? `${formData.subject.length}/150 - too long`
          : `${formData.subject.length}/150 characters`,
    },
    previewText: {
      valid: formData.previewText.length <= 200,
      message: formData.previewText.length > 200
        ? `${formData.previewText.length}/200 - too long`
        : `${formData.previewText.length}/200 characters (optional)`,
    },
    content: {
      valid: markdownContent.trim().length >= 1,
      message: markdownContent.trim().length === 0 ? "Add some content blocks" : `${blocks.length} blocks`,
    },
  }), [formData, markdownContent, blocks.length]);

  const isValid = validation.name.valid && validation.subject.valid && validation.previewText.valid && validation.content.valid;

  const missingFields = [
    !validation.name.valid && "Campaign Name",
    !validation.subject.valid && "Email Subject",
    !validation.content.valid && "Email Content",
  ].filter(Boolean) as string[];

  // Preview HTML
  const previewContent = useMemo(() => {
    const subject = interpolatePreview(formData.subject);
    const content = interpolatePreview(markdownContent);
    const htmlContent = contentToHtml(content);
    return {
      subject,
      previewText: formData.previewText,
      html: htmlContent,
    };
  }, [formData.subject, formData.previewText, markdownContent]);

  // Fetch existing campaign
  useEffect(() => {
    if (isEditing && id) {
      fetch(`/api/admin/campaigns/${id}`, { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch campaign");
          return res.json();
        })
        .then((data) => {
          const c = data as Campaign;
          setCampaign(c);
          setFormData({
            name: c.name,
            subject: c.subject,
            previewText: c.previewText || "",
            segment: c.segment,
            // The DB stores an absolute UTC instant. The datetime-local input
            // is naive (no timezone) and shows whatever string we hand it as
            // local time, so format the instant in the user's timezone before
            // slicing. Doing toISOString().slice() here would mis-display
            // every existing campaign by the browser's UTC offset.
            scheduledFor: c.scheduledFor ? utcInstantToLocalInputValue(new Date(c.scheduledFor)) : "",
          });
          setBlocks(markdownToBlocks(c.content));
          if (c.excludedEmails) {
            setExcludedEmails(JSON.parse(c.excludedEmails));
          }
        })
        .catch(() => {
          toast.error("Failed to load campaign");
          navigate("/admin/campaigns");
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);

  // Fetch customers and subscribers for recipient list
  useEffect(() => {
    Promise.all([
      fetch("/api/admin/customers", { credentials: "include" }).then(r => r.ok ? r.json() : []),
      fetch("/api/admin/subscribers", { credentials: "include" }).then(r => r.ok ? r.json() : []),
    ]).then(([customers, subscribers]) => {
      setAllCustomers(customers as CustomerSummary[]);
      setAllSubscribers(subscribers as { email: string; name: string | null; status: string }[]);
    }).catch(() => {});
  }, []);

  const handleSave = async (asDraft = true) => {
    if (!isValid) {
      toast.error("Please fix validation errors before saving");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name,
        subject: formData.subject,
        previewText: formData.previewText || null,
        content: markdownContent,
        segment: formData.segment,
        // datetime-local input gives "YYYY-MM-DDTHH:mm" with no timezone.
        // Construct a Date from it (browser interprets as local) and serialize
        // as ISO-with-Z so the worker (which runs in UTC) parses it as the
        // exact instant the user picked, not as that wall-clock time in UTC.
        scheduledFor: formData.scheduledFor
          ? new Date(formData.scheduledFor).toISOString()
          : null,
        status: asDraft ? "draft" : (formData.scheduledFor ? "scheduled" : "draft"),
        excludedEmails,
      };

      const url = isEditing ? `/api/admin/campaigns/${id}` : "/api/admin/campaigns";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const data = (await response.json()) as { error?: string };

      if (response.ok) {
        toast.success(isEditing ? "Campaign updated" : "Campaign created");
        navigate("/admin/campaigns");
      } else {
        toast.error(data.error || "Failed to save campaign");
      }
    } catch {
      toast.error("Failed to save campaign");
    } finally {
      setIsSaving(false);
    }
  };

  const applyTemplate = useCallback((templateIndex: number) => {
    const template = CAMPAIGN_TEMPLATES[templateIndex];
    if (!template) return;
    // Deep clone blocks with new IDs
    const newBlocks = template.blocks.map((b) => createBlock(b.type, {
      content: b.content,
      url: b.url,
      alt: b.alt,
      level: b.level,
    }));
    setBlocks(newBlocks);
    toast.success(`"${template.name}" template applied`);
  }, []);


  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FiLoader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Can't edit sent campaigns
  if (isEditing && campaign && (campaign.status === "sent" || campaign.status === "sending")) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate("/admin/campaigns")}>
          <FiArrowLeft className="w-4 h-4 mr-2" />
          Back to Campaigns
        </Button>
        <div className="border rounded-lg p-12 text-center bg-white">
          <FiAlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Cannot Edit This Campaign</h2>
          <p className="text-muted-foreground">
            This campaign has already been {campaign.status === "sent" ? "sent" : "is currently sending"} and cannot be edited.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col -m-6">
      {/* ── Top Bar ── */}
      <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-border bg-white rounded-t-2xl z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-foreground rounded-lg" onClick={() => navigate("/admin/campaigns")}>
            <FiArrowLeft className="w-4 h-4" />
            Campaigns
          </Button>
          <h1 className="font-serif text-xl font-medium text-foreground hidden sm:block">
            {isEditing ? formData.name || "Edit Campaign" : "New Campaign"}
          </h1>
          {countdown && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
              <FiClock className="w-3.5 h-3.5 text-amber-600" />
              <span className="text-xs font-semibold text-amber-700 tabular-nums">{countdown}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant={showPreview ? "default" : "outline"}
            size="sm"
            onClick={() => setShowPreview(!showPreview)}
            className="hidden lg:flex gap-1.5"
          >
            <FiEye className="w-3.5 h-3.5" />
            Preview
          </Button>
          {/* When a schedule date is set we promote "Schedule" to the
              primary button and demote "Save Draft" to outline. A draft
              with a future date in the input but saved as draft is the
              biggest source of "why didn't my campaign send?" confusion
              — make the active default match the intent. */}
          <div className="relative group/save">
            <Button
              variant={formData.scheduledFor ? "outline" : "default"}
              size="sm"
              onClick={() => handleSave(true)}
              disabled={isSaving || !isValid}
              className="gap-1.5"
              title={formData.scheduledFor ? "Save as draft — will NOT send on the scheduled date" : "Save as draft"}
            >
              {isSaving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSave className="w-4 h-4" />}
              {isSaving ? "Saving..." : "Save Draft"}
            </Button>
            {!isValid && missingFields.length > 0 && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-foreground text-primary-foreground text-xs rounded-lg p-3 shadow-lg opacity-0 pointer-events-none group-hover/save:opacity-100 transition-opacity z-50">
                <p className="font-semibold mb-1.5">Required to save:</p>
                <ul className="space-y-1">
                  {missingFields.map((f) => (
                    <li key={f} className="flex items-center gap-1.5">
                      <FiAlertCircle className="w-3 h-3 text-amber-400 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {formData.scheduledFor && (
            <Button
              variant="default"
              size="sm"
              onClick={() => handleSave(false)}
              disabled={isSaving || !isValid}
              className="gap-1.5"
              title="Save and schedule — will send at the chosen date/time"
            >
              <FiCalendar className="w-4 h-4" />
              Schedule
            </Button>
          )}
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className={`flex-1 min-h-0 overflow-hidden grid grid-cols-1 ${showPreview ? "lg:grid-cols-2" : ""}`}>

        {/* ── Left: Editor ── */}
        <div className="h-full overflow-y-auto bg-muted/30">
          <div className="max-w-2xl mx-auto p-6 md:p-8 space-y-6">

            {/* ── Step 1: Setup ── */}
            <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-6 py-4 bg-secondary/50 border-b border-border">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">1</div>
                <h2 className="font-serif text-lg font-medium text-foreground">Setup</h2>
              </div>

              <div className="p-6 space-y-5">
                {/* Template picker for new */}
                {!isEditing && (
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5 block">Start from a template</label>
                    <div className="grid grid-cols-3 gap-2">
                      {CAMPAIGN_TEMPLATES.map((t, i) => (
                        <button
                          key={t.name}
                          onClick={() => applyTemplate(i)}
                          className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-border bg-white hover:border-primary/40 hover:bg-primary/5 transition-all group"
                        >
                          <FiFileText className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                          <span className="text-xs font-medium text-foreground/70 group-hover:text-primary transition-colors">{t.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm font-medium text-foreground/70 mb-1.5 block">
                    Campaign Name
                    {!validation.name.valid && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Give this campaign a name (only you see this)"
                    className={!validation.name.valid ? "border-red-300 focus-visible:ring-red-400" : ""}
                  />
                  {!validation.name.valid && (
                    <p className="text-xs mt-1 text-red-500 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" /> Required
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/70 mb-1.5 block">
                    Email Subject Line
                    {!validation.subject.valid && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <Input
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    placeholder="What your recipients will see in their inbox"
                    className={!validation.subject.valid ? "border-red-300 focus-visible:ring-red-400" : ""}
                  />
                  {formData.subject.length === 0 ? (
                    <p className="text-xs mt-1 text-red-500 flex items-center gap-1">
                      <FiAlertCircle className="w-3 h-3" /> Required
                    </p>
                  ) : formData.subject.length > 0 && (
                    <p className={`text-xs mt-1 ${formData.subject.length > 150 ? "text-red-500" : "text-muted-foreground"}`}>
                      {formData.subject.length}/150 characters
                    </p>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-foreground/70 mb-1.5 block">
                    Preview Text
                    <span className="text-muted-foreground font-normal ml-1 text-xs">optional</span>
                  </label>
                  <Input
                    value={formData.previewText}
                    onChange={(e) => setFormData({ ...formData, previewText: e.target.value })}
                    placeholder="Short text shown next to the subject in most inboxes"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground/70 mb-1.5 block">Send to</label>
                    <Select
                      value={formData.segment}
                      onValueChange={(v) => setFormData({ ...formData, segment: v as FormData["segment"] })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {segmentOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground/70 mb-1.5 block">
                      Schedule
                      <span className="text-muted-foreground font-normal ml-1 text-xs">optional</span>
                    </label>
                    <Input
                      type="datetime-local"
                      value={formData.scheduledFor}
                      onChange={(e) => setFormData({ ...formData, scheduledFor: e.target.value })}
                      min={utcInstantToLocalInputValue(new Date())}
                    />
                    {countdown && (
                      <p className="text-xs font-medium text-amber-600 mt-1.5 flex items-center gap-1">
                        <FiClock className="w-3 h-3" />
                        Sends in {countdown}
                      </p>
                    )}
                    {/* Loud warning when a date is set but the campaign is
                        still a draft. This is the single most common
                        source of "why didn't it send?" — drafts are NOT
                        picked up by the cron, only scheduled campaigns
                        are. The user has to click the green Schedule
                        button on the top bar to commit. */}
                    {formData.scheduledFor && campaign?.status === "draft" && (
                      <div className="mt-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
                        <FiAlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <div className="text-xs leading-snug">
                          <p className="font-semibold text-amber-900">
                            This is still a draft — it won't send.
                          </p>
                          <p className="text-amber-800 mt-0.5">
                            You've picked a date but haven't scheduled yet. Click <strong>Schedule</strong> in the top bar to lock it in.
                          </p>
                        </div>
                      </div>
                    )}
                    <p className="text-[11px] text-muted-foreground mt-2 leading-snug">
                      Scheduled campaigns are picked up by a cron job that runs every 5 minutes. Actual send time may be delayed up to ~5 minutes after the scheduled time. Large recipient lists are queued and delivered in the background by the email-sender worker. To test immediately, save the campaign and click <strong>"Run now"</strong> on the Campaigns page.
                    </p>
                  </div>
                </div>

                {/* Individual recipient control */}
                {(() => {
                  const segment = formData.segment;
                  const customerTypeSegments = ["existing", "ineligible", "discharged"];
                  let recipients: { email: string; name: string | null; accountStatus?: string | null }[];
                  if (customerTypeSegments.includes(segment)) {
                    recipients = allCustomers
                      .filter((c) => c.accountStatus === segment)
                      .map((c) => ({ email: c.email, name: c.name, accountStatus: c.accountStatus }));
                  } else {
                    const activeSubscribers = allSubscribers.filter((s) => s.status === "active");
                    recipients = activeSubscribers.map((s) => ({ email: s.email, name: s.name }));
                    const customerByEmail = new Map(allCustomers.map((c) => [c.email.toLowerCase(), c]));
                    recipients = recipients.map((r) => ({
                      ...r,
                      accountStatus: customerByEmail.get(r.email.toLowerCase())?.accountStatus ?? null,
                    }));
                  }
                  const filtered = recipientSearch
                    ? recipients.filter((r) =>
                        r.name?.toLowerCase().includes(recipientSearch.toLowerCase()) ||
                        r.email.toLowerCase().includes(recipientSearch.toLowerCase())
                      )
                    : recipients;
                  const activeCount = recipients.length - excludedEmails.length;

                  return (
                    <div className="rounded-xl border border-border bg-secondary/30 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setShowRecipientList(!showRecipientList)}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-secondary/60 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <FiUsers className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm font-medium text-foreground/80">Recipients</span>
                          <span className="text-xs text-muted-foreground">
                            {activeCount} of {recipients.length}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {excludedEmails.length > 0 && (
                            <span className="text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                              {excludedEmails.length} excluded
                            </span>
                          )}
                          <FiArrowDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${showRecipientList ? "rotate-180" : ""}`} />
                        </div>
                      </button>

                      {showRecipientList && (
                        <>
                          <div className="px-3 pb-2 border-t border-border/50 pt-2 space-y-2">
                            <div className="relative">
                              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-3.5 h-3.5" />
                              <Input
                                placeholder="Search recipients..."
                                value={recipientSearch}
                                onChange={(e) => setRecipientSearch(e.target.value)}
                                className="pl-9 h-9 text-sm bg-white"
                              />
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <button
                                type="button"
                                onClick={() => {
                                  const filteredLower = filtered.map(r => r.email.toLowerCase());
                                  setExcludedEmails(excludedEmails.filter(e => !filteredLower.includes(e)));
                                }}
                                className="px-2.5 py-1 rounded-md border border-border bg-white hover:bg-secondary transition-colors font-medium"
                              >
                                Select all{recipientSearch ? " (filtered)" : ""}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const filteredLower = filtered.map(r => r.email.toLowerCase());
                                  const merged = new Set([...excludedEmails, ...filteredLower]);
                                  setExcludedEmails(Array.from(merged));
                                }}
                                className="px-2.5 py-1 rounded-md border border-border bg-white hover:bg-secondary transition-colors font-medium"
                              >
                                Deselect all{recipientSearch ? " (filtered)" : ""}
                              </button>
                              <span className="ml-auto text-muted-foreground">
                                {filtered.length} shown
                              </span>
                            </div>
                          </div>
                          <div className="max-h-52 overflow-y-auto">
                            {filtered.length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-6">
                                {recipientSearch ? "No recipients match search" : "No recipients in this segment"}
                              </p>
                            ) : (
                              filtered.map((recipient) => {
                                const isExcluded = excludedEmails.includes(recipient.email.toLowerCase());
                                return (
                                  <div
                                    key={recipient.email}
                                    className={`group flex items-center gap-3 px-4 py-2.5 border-t border-border/30 hover:bg-white/60 transition-colors ${isExcluded ? "opacity-40" : ""}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!isExcluded}
                                      onChange={() => {
                                        const emailLower = recipient.email.toLowerCase();
                                        setExcludedEmails(
                                          isExcluded
                                            ? excludedEmails.filter((e) => e !== emailLower)
                                            : [...excludedEmails, emailLower]
                                        );
                                      }}
                                      className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                                    />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-foreground truncate">{recipient.name || recipient.email}</p>
                                      {recipient.name && (
                                        <p className="text-xs text-muted-foreground truncate">{recipient.email}</p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      title="Send only to this recipient (excludes all others in segment)"
                                      onClick={() => {
                                        const keep = recipient.email.toLowerCase();
                                        const allOthers = recipients
                                          .map(r => r.email.toLowerCase())
                                          .filter(e => e !== keep);
                                        setExcludedEmails(allOthers);
                                      }}
                                      className="text-[11px] px-2 py-0.5 rounded-md border border-border bg-white hover:bg-primary hover:text-primary-foreground transition-colors font-medium opacity-0 group-hover:opacity-100"
                                    >
                                      Only this
                                    </button>
                                    {recipient.accountStatus && (
                                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium shrink-0 ${
                                        recipient.accountStatus === "existing" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                                        recipient.accountStatus === "ineligible" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                                        "bg-slate-50 text-slate-600 border border-slate-200"
                                      }`}>
                                        {recipient.accountStatus}
                                      </span>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </section>

            {/* ── Step 2: Content ── */}
            <section className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-6 py-4 bg-secondary/50 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-[11px] font-bold">2</div>
                  <h2 className="font-serif text-lg font-medium text-foreground">Email Content</h2>
                </div>
                {isValid ? (
                  <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-3 py-1 rounded-full flex items-center gap-1 font-medium">
                    <FiCheck className="w-3 h-3" /> Ready to send
                  </span>
                ) : (
                  <span className="text-xs text-muted-foreground font-medium">
                    {blocks.length} block{blocks.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              <div className="p-6 space-y-2.5">
                <BlockEditorPanel blocks={blocks} onBlocksChange={setBlocks} />
              </div>
            </section>
          </div>
        </div>

        {/* ── Right: Live Preview ── */}
        {showPreview && (
          <div className="hidden lg:flex flex-col h-full overflow-hidden border-l border-border bg-muted/40">
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 pb-12">
                {/* Inbox simulation */}
                <div className="rounded-2xl shadow-lg overflow-hidden border border-border max-w-xl mx-auto bg-white">
                  {/* From / Subject bar */}
                  <div className="px-5 py-4 border-b bg-white">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0" style={{ backgroundColor: EMAIL_BRAND.primary }}>
                        PF
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{EMAIL_BRAND.companyName}</p>
                        <p className="text-xs text-slate-400">hello@pathfinancial.com.au</p>
                      </div>
                    </div>
                    <p className="font-semibold text-slate-900">
                      {previewContent.subject || "Your subject line will appear here"}
                    </p>
                    {previewContent.previewText && (
                      <p className="text-sm text-slate-400 mt-0.5 truncate">{previewContent.previewText}</p>
                    )}
                  </div>

                  {/* Email body */}
                  <div style={{ backgroundColor: EMAIL_BRAND.cream, padding: '24px 16px' }}>
                    <div style={{ maxWidth: 560, margin: '0 auto', backgroundColor: EMAIL_BRAND.white, borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      {/* Logo */}
                      <div style={{ textAlign: 'center', padding: '24px 32px 16px', backgroundColor: EMAIL_BRAND.white }}>
                        <img src={EMAIL_BRAND.logoUrl} alt={EMAIL_BRAND.companyName} style={{ display: 'block', width: 180, maxWidth: '100%', height: 'auto', margin: '0 auto' }} />
                      </div>
                      <div style={{ padding: '0 32px' }}><div style={{ borderTop: `3px solid ${EMAIL_BRAND.primary}` }} /></div>

                      {/* Content */}
                      <div style={{ padding: '28px 32px 32px' }}>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: previewContent.html || `<p style="color: ${EMAIL_BRAND.textLight}; font-style: italic;">Start adding content blocks on the left to see your email here.</p>`,
                          }}
                        />
                      </div>

                      {/* Footer */}
                      <div style={{ padding: '0 32px' }}><div style={{ borderTop: `1px solid ${EMAIL_BRAND.border}` }} /></div>
                      <div style={{ padding: '20px 32px', backgroundColor: EMAIL_BRAND.cream, textAlign: 'center', fontSize: 12, color: EMAIL_BRAND.textLight, lineHeight: 1.6 }}>
                        <p style={{ margin: '0 0 4px 0' }}><strong style={{ color: EMAIL_BRAND.primary }}>{EMAIL_BRAND.companyName}</strong></p>
                        <p style={{ margin: '0 0 4px 0' }}>ACL {EMAIL_BRAND.acl} | ABN {EMAIL_BRAND.abn}</p>
                        <p style={{ margin: '0 0 8px 0' }}>{EMAIL_BRAND.address}</p>
                        <p style={{ margin: 0 }}>
                          <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: 'underline', fontSize: 11 }}>Manage Preferences</a>
                          <span style={{ margin: '0 6px', color: '#ccc' }}>|</span>
                          <a href="#" style={{ color: EMAIL_BRAND.accent, textDecoration: 'underline', fontSize: 11 }}>Unsubscribe</a>
                        </p>
                      </div>
                      <div style={{ textAlign: 'center', padding: '8px 32px 16px' }}>
                        <p style={{ margin: 0, fontSize: 10, color: EMAIL_BRAND.textLight }}>
                          &copy; {new Date().getFullYear()} {EMAIL_BRAND.companyName}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <p className="text-center text-[11px] text-slate-400 mt-4">
                  Variables like {"{{firstName}}"} show as sample data in preview
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
